import React, { useState } from 'react';
import styled from 'styled-components';
import Window from '../Window';
import { 
  FolderOpen, 
  FileText, 
  Github, 
  ExternalLink,
  User,
  Code,
  Image,
  ChevronRight,
  Home,
  Monitor,
  Download,
  Trash2,
  Globe
} from 'lucide-react';

const FinderContainer = styled.div`
  display: flex;
  height: 100%;
  background: #f5f5f7;
`;

const Sidebar = styled.div`
  width: 200px;
  background: rgba(255, 255, 255, 0.8);
  border-right: 1px solid rgba(0, 0, 0, 0.1);
  padding: 16px 0;
`;

const SidebarSection = styled.div`
  margin-bottom: 24px;
`;

const SidebarTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  padding: 0 16px;
`;

const SidebarItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(0, 122, 255, 0.1);
  }
  
  &.active {
    background: rgba(0, 122, 255, 0.2);
    color: #007AFF;
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.8);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const ToolbarButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: none;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  font-size: 12px;
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const PathBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.5);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  font-size: 12px;
  color: #666;
`;

const PathSegment = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  
  &:hover {
    color: #007AFF;
  }
  
  &:not(:last-child)::after {
    content: '>';
    margin-left: 8px;
    color: #999;
  }
`;

const FileGrid = styled.div`
  flex: 1;
  padding: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 20px;
  overflow-y: auto;
`;

const FileItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 12px 8px;
  border-radius: 8px;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
  
  &.selected {
    background: rgba(0, 122, 255, 0.1);
  }
`;

const FileIcon = styled.div`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  position: relative;
  color: ${props => props.color || '#333'};
`;

const FileName = styled.div`
  font-size: 11px;
  color: #333;
  text-align: center;
  max-width: 100px;
  word-wrap: break-word;
  line-height: 1.2;
`;

const FinderApp = ({ onOpenTextFile, ...windowProps }) => {
  const [currentPath, setCurrentPath] = useState('Desktop');
  const [selectedFile, setSelectedFile] = useState(null);

  const sidebarItems = [
    {
      title: 'Favorites',
      items: [
        { name: 'Desktop', icon: <Monitor size={16} />, path: 'Desktop' },
        { name: 'Downloads', icon: <Download size={16} />, path: 'Downloads' },
        { name: 'Trash', icon: <Trash2 size={16} />, path: 'Trash' }
      ]
    },
    {
      title: 'Categories',
      items: [
        { name: 'Work', icon: <FolderOpen size={16} />, path: 'Work' },
        { name: 'About Me', icon: <User size={16} />, path: 'About Me' },
        { name: 'Resume', icon: <FileText size={16} />, path: 'Resume' }
      ]
    }
  ];

  const desktopFiles = [
    {
      name: "Atithi - Travel Platform",
      icon: <FolderOpen size={32} />,
      color: "#FFD700",
      type: "folder"
    },
    {
      name: "CommuniTea Chat App",
      icon: <FolderOpen size={32} />,
      color: "#FF6B6B",
      type: "folder"
    },
    {
      name: "Speech-to-Text App",
      icon: <FolderOpen size={32} />,
      color: "#4ECDC4",
      type: "folder"
    },
    {
      name: "Resume.pdf",
      icon: <FileText size={32} />,
      color: "#FF3B30",
      type: "file",
      link: "/resume.pdf"
    },
    {
      name: "About Me",
      icon: <FolderOpen size={32} />,
      color: "#007AFF",
      type: "folder"
    }
  ];

  const projectFiles = {
    "Atithi - Travel Platform": [
      {
        name: "Demo",
        icon: <Globe size={32} />,
        color: "#007AFF",
        type: "demo",
        link: "https://atithi-travel-demo.com"
      },
      {
        name: "description.txt",
        icon: <FileText size={32} />,
        color: "#333",
        type: "text",
        content: "A scalable full-stack travel platform built with React/Node.js. Features JWT authentication, AWS S3 media handling, and real-time booking system. Reduced API latency by 25% in stress tests."
      }
    ],
    "CommuniTea Chat App": [
      {
        name: "Demo",
        icon: <Globe size={32} />,
        color: "#007AFF",
        type: "demo",
        link: "https://communitea-chat.herokuapp.com"
      },
      {
        name: "description.txt",
        icon: <FileText size={32} />,
        color: "#333",
        type: "text",
        content: "Real-time chat application built with Django Channels and WebSockets. Handles 50+ concurrent users with <250ms message delivery latency. Features user registration and profile management."
      }
    ],
    "Speech-to-Text App": [
      {
        name: "Demo",
        icon: <Globe size={32} />,
        color: "#007AFF",
        type: "demo",
        link: "https://speech-to-text-demo.com"
      },
      {
        name: "description.txt",
        icon: <FileText size={32} />,
        color: "#333",
        type: "text",
        content: "Advanced speech recognition application using Python, Django, React, and Faster Whisper. Provides real-time transcription with high accuracy and supports multiple languages."
      }
    ]
  };

  const handleFileClick = (file) => {
    setSelectedFile(file);
    
    if (file.type === 'demo' && file.link) {
      window.open(file.link, '_blank');
    } else if (file.type === 'text' && file.content && onOpenTextFile) {
      onOpenTextFile(file.name, file.content);
    } else if (file.type === 'file' && file.link) {
      window.open(file.link, '_blank');
    } else if (file.type === 'folder') {
      // Navigate to the folder
      setCurrentPath(file.name);
    }
  };

  const handleSidebarItemClick = (item) => {
    setCurrentPath(item.path);
    setSelectedFile(null);
  };

  const handlePathSegmentClick = (path) => {
    setCurrentPath(path);
    setSelectedFile(null);
  };

  const getCurrentFiles = () => {
    if (currentPath === 'Desktop') {
      return desktopFiles;
    } else if (projectFiles[currentPath]) {
      return projectFiles[currentPath];
    }
    return [];
  };

  const getPathSegments = () => {
    if (currentPath === 'Desktop') {
      return ['Desktop'];
    } else if (projectFiles[currentPath]) {
      return ['Desktop', currentPath];
    }
    return [currentPath];
  };

  return (
    <Window {...windowProps} size={{ width: 800, height: 500 }}>
      <FinderContainer>
        <Sidebar>
          {sidebarItems.map((section, index) => (
            <SidebarSection key={index}>
              <SidebarTitle>{section.title}</SidebarTitle>
              {section.items.map((item, itemIndex) => (
                <SidebarItem
                  key={itemIndex}
                  className={currentPath === item.path ? 'active' : ''}
                  onClick={() => handleSidebarItemClick(item)}
                >
                  {item.icon}
                  {item.name}
                </SidebarItem>
              ))}
            </SidebarSection>
          ))}
        </Sidebar>
        
        <MainContent>
          <Toolbar>
            <ToolbarButton>
              <ChevronRight size={14} />
              Back
            </ToolbarButton>
            <ToolbarButton>
              <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} />
              Forward
            </ToolbarButton>
            <ToolbarButton>
              <FolderOpen size={14} />
              New Folder
            </ToolbarButton>
          </Toolbar>
          
          <PathBar>
            {getPathSegments().map((segment, index) => (
              <PathSegment 
                key={index}
                onClick={() => handlePathSegmentClick(segment)}
              >
                {index === 0 && <Home size={12} />}
                {segment}
              </PathSegment>
            ))}
          </PathBar>
          
          <FileGrid>
            {getCurrentFiles().map((file, index) => (
              <FileItem
                key={index}
                className={selectedFile === file ? 'selected' : ''}
                onClick={() => handleFileClick(file)}
              >
                <FileIcon color={file.color}>
                  {file.icon}
                  {file.link && file.type !== 'demo' && (
                    <div style={{ position: 'absolute', bottom: 0, right: 0, fontSize: '12px' }}>
                      {file.link.includes('github.com') ? (
                        <Github size={12} style={{ color: '#333' }} />
                      ) : (
                        <ExternalLink size={12} style={{ color: '#007AFF' }} />
                      )}
                    </div>
                  )}
                </FileIcon>
                <FileName>{file.name}</FileName>
              </FileItem>
            ))}
          </FileGrid>
        </MainContent>
      </FinderContainer>
    </Window>
  );
};

export default FinderApp;
